import type { AnalysisState } from '@aibi/contracts';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  cardRect,
  clampCardX,
  clampCardY,
  findFreePosition,
  GRID_COLUMNS,
  isRectFree,
  ROW_UNIT_REM,
  type GridRect,
  type WorkspaceDocument,
} from '../../model/workspace-document';
import type { WorkspaceCommand } from '../../model/workspace-reducer';

/** Pitch de columna a partir del ancho del grid (gap incluido). */
export function columnPitchOf(grid: HTMLElement): number {
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
  return (grid.clientWidth - gap * (GRID_COLUMNS - 1)) / GRID_COLUMNS + gap;
}

export function rowPitchOf(grid: HTMLElement): number {
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.rowGap || styles.gap || '0') || 0;
  const rootFont = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return ROW_UNIT_REM * rootFont + gap;
}

/** Tamano en px segun el w/h actual de la tarjeta en la rejilla. */
export function cardPixelSize(
  card: AnalysisState,
  grid: HTMLElement | null,
): { readonly widthPx: number; readonly heightPx: number } {
  const { w, h } = cardRect(card);
  const rootFont = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const rowTrack = ROW_UNIT_REM * rootFont;
  if (grid === null) {
    return {
      widthPx: Math.max(48, w * 72),
      heightPx: Math.max(40, h * rowTrack),
    };
  }
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
  const rowGap = Number.parseFloat(styles.rowGap || styles.gap || '0') || 0;
  const colPitch = columnPitchOf(grid);
  return {
    widthPx: Math.max(48, w * colPitch - gap),
    heightPx: Math.max(40, h * rowTrack + Math.max(0, h - 1) * rowGap),
  };
}

/** Preferir el rect del DOM (tamano pintado) y caer al calculo por rejilla. */
export function measureCardPixels(
  cardId: string,
  card: AnalysisState,
  grid: HTMLElement | null = globalThis.document.querySelector<HTMLElement>('[data-canvas-grid]'),
): { readonly widthPx: number; readonly heightPx: number } {
  const node = globalThis.document.querySelector<HTMLElement>(
    `[data-card-id="${CSS.escape(cardId)}"]`,
  );
  const box = node?.getBoundingClientRect();
  if (box && box.width > 1 && box.height > 1) {
    return { widthPx: box.width, heightPx: box.height };
  }
  return cardPixelSize(card, grid);
}

export function pointerFromDelta(
  activatorEvent: Event,
  delta: { readonly x: number; readonly y: number },
): { x: number; y: number } | null {
  if (!('clientX' in activatorEvent) || !('clientY' in activatorEvent)) return null;
  const point = activatorEvent as PointerEvent;
  if (!Number.isFinite(point.clientX) || !Number.isFinite(point.clientY)) return null;
  return { x: point.clientX + delta.x, y: point.clientY + delta.y };
}

/** Celda de encaje bajo el puntero (conserva w/h actuales; sin solapar). */
export function snapCardAtPointer(
  pointer: { readonly x: number; readonly y: number },
  cardId: string,
  workspace: WorkspaceDocument,
  grid: HTMLElement | null = globalThis.document.querySelector<HTMLElement>('[data-canvas-grid]'),
): GridRect | null {
  const card = workspace.cards.find((entry) => entry.id === cardId);
  if (!card || grid === null) return null;
  const box = grid.getBoundingClientRect();
  const colPitch = columnPitchOf(grid);
  const rowPitch = rowPitchOf(grid);
  const current = cardRect(card);
  const desired = {
    x: clampCardX(Math.round((pointer.x - box.left) / colPitch - current.w / 2), current.w),
    y: clampCardY(Math.round((pointer.y - box.top) / rowPitch)),
    w: current.w,
    h: current.h,
  };
  const occupied = workspace.cards.filter((entry) => entry.id !== cardId).map(cardRect);
  return isRectFree(occupied, desired)
    ? desired
    : findFreePosition(workspace.cards, current.w, current.h, desired, cardId);
}

/**
 * Traduce el soltado de una tarjeta sobre el lienzo a `place-card` en coordenadas de
 * rejilla, conservando el tamano actual.
 */
export function placeCardFromDrag(
  event: DragEndEvent,
  cardId: string,
  workspace: WorkspaceDocument,
): WorkspaceCommand | null {
  const card = workspace.cards.find((entry) => entry.id === cardId);
  if (!card) return null;
  const pointer = pointerFromDelta(event.activatorEvent, event.delta);
  if (pointer === null) return null;
  const grid = globalThis.document.querySelector<HTMLElement>('[data-canvas-grid]');
  if (grid === null) return null;
  const snap = snapCardAtPointer(pointer, cardId, workspace, grid);
  if (snap === null) return null;
  const current = cardRect(card);
  if (current.x === snap.x && current.y === snap.y) return null;
  return { type: 'place-card', cardId, x: snap.x, y: snap.y };
}
